import { useEffect, useRef, useState } from 'react';

import { Icon } from '../Icon';
import { Switch } from '../Switch';
import styles from './DemoDataPanel.module.css';

export type DemoDataMode = 'demo' | 'initial';

export interface DemoDataToggleItem {
  /** 该开关属于哪个页面／模块。只用于可访问名称，界面上统一显示「模拟数据」。 */
  key: string;
  label: string;
  mode: DemoDataMode;
  onModeChange: (mode: DemoDataMode) => void;
}

export interface DemoDataPanelProps {
  /** 一行一个页面的「模拟数据」开关；后续页面要演示数据开关，直接再追加一行。 */
  items: readonly DemoDataToggleItem[];
}

/** 每个页面只挂一个自己的实例，界面上不必再用页面名区分，统一叫「模拟数据」。 */
const VISIBLE_LABEL = '模拟数据';

/*
 * 演示数据开关：右下角悬浮小部件，默认只留一个圆形配置按钮，点击才弹出配置气泡。
 * 每行控制一个页面的数据形态：开启＝模拟数据，关闭＝初始状态（如新校区开通时的最小数据集）。
 */
export function DemoDataPanel({ items }: DemoDataPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  /* 气泡是临时操作面板：点到页面任意其他地方就收起，不占用视野。 */
  useEffect(() => {
    if (!expanded) return;

    function handlePointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setExpanded(false);
    }

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [expanded]);

  return <div className={styles.root} ref={rootRef}>
    {expanded ? <div className={styles.panel}>
      {items.map((item) => (
        <div className={styles.row} key={item.key}>
          <span className={styles.rowLabel}>{VISIBLE_LABEL}</span>
          <Switch
            checked={item.mode === 'demo'}
            label={`${item.label}模拟数据；关闭后展示初始状态数据`}
            onCheckedChange={(checked) => item.onModeChange(checked ? 'demo' : 'initial')}
            size="small"
          />
        </div>
      ))}
    </div> : null}
    <button
      aria-expanded={expanded}
      aria-label={expanded ? '收起演示数据设置' : '展开演示数据设置'}
      className={styles.trigger}
      onClick={() => setExpanded((open) => !open)}
      type="button"
    ><Icon name="list-check" size="base" /></button>
  </div>;
}
